import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot, where, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * HoppHomes - Lead Management Dashboard for Super Admin
 * 
 * Features:
 * - Real-time lead list with scoring and tier
 * - Filters (tier, timeline, financing, logistics, plot)
 * - Alerts for A-leads and logistics issues
 * - Charts for analytics
 * - Lead detail modal with checklist
 */

const LeadManagementDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    tier: 'all',
    timeline: 'all',
    financing: 'all',
    logistics: 'all',
    plot: 'all'
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    tierA: 0,
    tierB: 0,
    tierC: 0,
    avgScore: 0,
    logisticsIssues: 0,
    newToday: 0
  });

  useEffect(() => {
    const db = getFirestore();
    const leadsRef = collection(db, 'leads');
    const q = query(leadsRef, orderBy('createdAt', 'desc'), limit(100));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leadsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));

      setLeads(leadsData);
      setFilteredLeads(leadsData);
      calculateStats(leadsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, leads]);

  const calculateStats = (leadsData) => {
    const total = leadsData.length;
    const tierA = leadsData.filter(l => l.tier === 'A').length;
    const tierB = leadsData.filter(l => l.tier === 'B').length;
    const tierC = leadsData.filter(l => l.tier === 'C').length;
    const avgScore = total > 0 ? Math.round(leadsData.reduce((sum, l) => sum + (l.score || 0), 0) / total) : 0;
    const logisticsIssues = leadsData.filter(l => l.logistics_blocker).length;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newToday = leadsData.filter(l => l.createdAt >= today).length;

    setStats({ total, tierA, tierB, tierC, avgScore, logisticsIssues, newToday });
  };

  const applyFilters = () => {
    let filtered = [...leads];

    if (filters.tier !== 'all') {
      filtered = filtered.filter(l => l.tier === filters.tier);
    }

    if (filters.timeline !== 'all') {
      filtered = filtered.filter(l => l.timeline === filters.timeline);
    }

    if (filters.financing !== 'all') {
      filtered = filtered.filter(l => l.financing === filters.financing);
    }

    if (filters.logistics !== 'all') {
      if (filters.logistics === 'ok') {
        filtered = filtered.filter(l => !l.logistics_blocker);
      } else if (filters.logistics === 'check') {
        filtered = filtered.filter(l => l.logistics_blocker);
      }
    }

    if (filters.plot !== 'all') {
      if (filters.plot === 'yes') {
        filtered = filtered.filter(l => l.plotStatus === 'own' || l.plotStatus === 'family');
      } else if (filters.plot === 'no') {
        filtered = filtered.filter(l => l.plotStatus === 'none');
      }
    }

    setFilteredLeads(filtered);
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'A': return 'bg-green-100 text-green-800 border-green-300';
      case 'B': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'C': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getTimelineLabel = (timeline) => {
    const labels = {
      '6m': '6 hó',
      '9m': '9 hó',
      '12m': '12 hó',
      '12p': '12+ hó',
      'unsure': 'bizonytalan'
    };
    return labels[timeline] || timeline;
  };

  const getFinancingLabel = (financing) => {
    const labels = {
      'cash': 'Készpénz',
      'mixed': 'Vegyes',
      'need_financing': 'Finanszírozás'
    };
    return labels[financing] || financing;
  };

  const getPlotLabel = (plotStatus) => {
    const labels = {
      'own': 'Saját',
      'family': 'Családi',
      'none': 'Nincs'
    };
    return labels[plotStatus] || plotStatus;
  };

  const manualSyncToGHL = async (leadId) => {
    try {
      const functions = getFunctions();
      const manualSync = httpsCallable(functions, 'manualSyncLeadToGHL');
      await manualSync({ leadId });
      alert('Sikeres GHL szinkronizáció!');
    } catch (error) {
      console.error('Error syncing to GHL:', error);
      alert('Hiba a GHL szinkronizáció során!');
    }
  };

  const renderAlerts = () => {
    const aLeadsToday = leads.filter(l => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return l.tier === 'A' && l.createdAt >= today;
    });

    const logisticsIssueLeads = leads.filter(l => l.logistics_blocker && l.tier !== 'C');

    return (
      <div className="space-y-3 mb-6">
        {aLeadsToday.length > 0 && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔥</span>
              <div>
                <h3 className="font-bold text-green-800">
                  {aLeadsToday.length} új A-lead ma!
                </h3>
                <p className="text-sm text-green-700">
                  {aLeadsToday.map(l => l.name).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {logisticsIssueLeads.length > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg cursor-pointer hover:bg-yellow-100 transition"
            onClick={() => setFilters({ ...filters, logistics: 'check' })}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-bold text-yellow-800">
                  {logisticsIssueLeads.length} lead logisztikai ellenőrzés szükséges
                </h3>
                <p className="text-sm text-yellow-700">
                  Kattints a szűréshez →
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStats = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">Összes lead</div>
        <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
        <div className="text-xs text-green-600 mt-1">+{stats.newToday} ma</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">A-tier leadek</div>
        <div className="text-3xl font-bold text-green-600">{stats.tierA}</div>
        <div className="text-xs text-gray-500 mt-1">
          {stats.total > 0 ? Math.round((stats.tierA / stats.total) * 100) : 0}% összes
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">Átlag pontszám</div>
        <div className="text-3xl font-bold text-blue-600">{stats.avgScore}</div>
        <div className="text-xs text-gray-500 mt-1">/ 120</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">Logisztikai issue</div>
        <div className="text-3xl font-bold text-yellow-600">{stats.logisticsIssues}</div>
        <div className="text-xs text-gray-500 mt-1">ellenőrzés kell</div>
      </div>
    </div>
  );

  const renderFilters = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <h3 className="font-bold text-gray-800 mb-4">Szűrők</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Tier</label>
          <select
            value={filters.tier}
            onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Összes</option>
            <option value="A">A-tier</option>
            <option value="B">B-tier</option>
            <option value="C">C-tier</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Időzítés</label>
          <select
            value={filters.timeline}
            onChange={(e) => setFilters({ ...filters, timeline: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Összes</option>
            <option value="6m">6 hó</option>
            <option value="9m">9 hó</option>
            <option value="12m">12 hó</option>
            <option value="12p">12+ hó</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Finanszírozás</label>
          <select
            value={filters.financing}
            onChange={(e) => setFilters({ ...filters, financing: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Összes</option>
            <option value="cash">Készpénz</option>
            <option value="mixed">Vegyes</option>
            <option value="need_financing">Finanszírozás</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Logisztika</label>
          <select
            value={filters.logistics}
            onChange={(e) => setFilters({ ...filters, logistics: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Összes</option>
            <option value="ok">OK</option>
            <option value="check">Ellenőrizendő</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Telek</label>
          <select
            value={filters.plot}
            onChange={(e) => setFilters({ ...filters, plot: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Összes</option>
            <option value="yes">Van</option>
            <option value="no">Nincs</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderLeadsList = () => (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Név</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Score</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Tier</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Időzítés</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Finanszírozás</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Telek</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Logisztika</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredLeads.map((lead) => (
              <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedLead(lead);
                  setShowDetailModal(true);
                }}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{lead.name}</div>
                  <div className="text-xs text-gray-500">{lead.email}</div>
                  <div className="text-xs text-gray-500">{lead.phone}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          lead.tier === 'A' ? 'bg-green-500' :
                          lead.tier === 'B' ? 'bg-yellow-500' : 'bg-gray-400'
                        }`}
                        style={{ width: `${(lead.score / 120) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{lead.score}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getTierBadgeColor(lead.tier)}`}>
                    {lead.tier}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{getTimelineLabel(lead.timeline)}</td>
                <td className="px-4 py-3 text-sm">{getFinancingLabel(lead.financing)}</td>
                <td className="px-4 py-3 text-sm">{getPlotLabel(lead.plotStatus)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xl ${lead.truckAccess === 'yes' ? '🚚' : lead.truckAccess === 'tight' ? '⚠️' : '❌'}`} title={`Kamion: ${lead.truckAccess}`} />
                    <span className={`text-xl ${lead.powerLineClearance5m === 'yes' ? '⚡' : '❌'}`} title={`Kábel: ${lead.powerLineClearance5m}`} />
                  </div>
                  {lead.logistics_blocker && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Check</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://app.gohighlevel.com/v2/location/${lead.ghlContactId}`, '_blank');
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    disabled={!lead.ghlContactId}
                  >
                    {lead.ghlContactId ? '🔗 GHL' : '⏳'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredLeads.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Nincs találat a szűrési feltételekkel.
        </div>
      )}
    </div>
  );

  const renderDetailModal = () => {
    if (!showDetailModal || !selectedLead) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowDetailModal(false)} />
        
        <div className="relative z-10 bg-white rounded-lg shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedLead.name}</h2>
                <p className="text-gray-600">{selectedLead.email} • {selectedLead.phone}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full"
              >
                ✕
              </button>
            </div>

            {/* Score */}
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg p-6 mb-6">
              <div className="text-sm font-medium mb-2">Lead pontszám</div>
              <div className="text-5xl font-bold">{selectedLead.score}/120</div>
              <div className="mt-2 text-lg">Tier: <span className="font-bold">{selectedLead.tier}</span></div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-xs text-gray-600 mb-1">Időzítés</div>
                <div className="font-medium">{getTimelineLabel(selectedLead.timeline)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Finanszírozás</div>
                <div className="font-medium">{getFinancingLabel(selectedLead.financing)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Költségkeret</div>
                <div className="font-medium">{selectedLead.budgetRange} M Ft</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Telek státusz</div>
                <div className="font-medium">{getPlotLabel(selectedLead.plotStatus)}</div>
              </div>
            </div>

            {/* Installation Checklist */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="font-bold text-gray-800 mb-3">📋 Telepítési checklist</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedLead.truckAccess === 'yes'}
                    readOnly
                    className="w-5 h-5"
                  />
                  <span className="text-sm">Kamion beállás megoldott</span>
                  <span className="ml-auto text-xs px-2 py-1 bg-gray-200 rounded">
                    {selectedLead.truckAccess}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedLead.powerLineClearance5m === 'yes'}
                    readOnly
                    className="w-5 h-5"
                  />
                  <span className="text-sm">5 m légkábel magasság rendben</span>
                  <span className="ml-auto text-xs px-2 py-1 bg-gray-200 rounded">
                    {selectedLead.powerLineClearance5m}
                  </span>
                </div>
              </div>
            </div>

            {/* Flags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedLead.financing_needed && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  💰 Finanszírozás szükséges
                </span>
              )}
              {selectedLead.no_plot_yet && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                  🏞️ Telek keresés
                </span>
              )}
              {selectedLead.logistics_blocker && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                  ⚠️ Logisztikai ellenőrzés
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => manualSyncToGHL(selectedLead.id)}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                🔄 GHL szinkronizálás
              </button>
              <button
                onClick={() => window.open(`tel:${selectedLead.phone}`)}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                📞 Hívás
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCharts = () => {
    // Tier distribution
    const tierData = [
      { name: 'A-tier', value: stats.tierA, color: '#10b981' },
      { name: 'B-tier', value: stats.tierB, color: '#f59e0b' },
      { name: 'C-tier', value: stats.tierC, color: '#6b7280' }
    ];

    // Timeline distribution
    const timelineData = [
      { name: '6 hó', count: leads.filter(l => l.timeline === '6m').length },
      { name: '9 hó', count: leads.filter(l => l.timeline === '9m').length },
      { name: '12 hó', count: leads.filter(l => l.timeline === '12m').length },
      { name: '12+ hó', count: leads.filter(l => l.timeline === '12p').length }
    ];

    // Financing distribution
    const financingData = [
      { name: 'Készpénz', count: leads.filter(l => l.financing === 'cash').length },
      { name: 'Vegyes', count: leads.filter(l => l.financing === 'mixed').length },
      { name: 'Finanszírozás', count: leads.filter(l => l.financing === 'need_financing').length }
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-bold text-gray-800 mb-4">Tier eloszlás</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={tierData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60}>
                {tierData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-bold text-gray-800 mb-4">Időzítés eloszlás</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-bold text-gray-800 mb-4">Finanszírozás típusok</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={financingData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">HoppHomes Lead Management</h1>
        <p className="text-gray-600">Lead kvalifikáció és kezelés</p>
      </div>

      {renderAlerts()}
      {renderStats()}
      {renderFilters()}
      {renderLeadsList()}
      {renderCharts()}
      {renderDetailModal()}
    </div>
  );
};

export default LeadManagementDashboard;
